import React, { useState } from 'react';
import {
  Combobox,
  Group,
  Text,
  Avatar,
  Stack,
  Loader,
  InputBase,
  useCombobox,
  Badge
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { masterUserAdminService } from 'services/dacum/masterUserAdminService';
import { EmplecUser } from 'services/api';

/* eslint-disable */
interface UserSelectorProps {
  value?: string;
  onChange: (user: EmplecUser | null) => void;
  error?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
}
/* eslint-enable */

interface ExtendedEmplecUser extends EmplecUser {
  sourceType: 'employee' | 'lecturer';
  uniqueKey: string;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  onChange,
  error,
  label = 'Select User',
  placeholder = 'Search and select user...',
  required = false
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption()
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchTerm, 500);
  const [selectedUser, setSelectedUser] = useState<ExtendedEmplecUser | null>(null);

  // Fetch users based on search term
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['emplecUsers', debouncedSearch],
    queryFn: () =>
      masterUserAdminService.getEmplecList({
        search: debouncedSearch,
        isEmployee: true,
        isLecturer: true
      }),
    enabled: debouncedSearch.length >= 2,
    select: (data) => {
      const employees = (data.employees?.data || []).map((user: any) => ({
        ...user,
        sourceType: 'employee' as const,
        uniqueKey: `${user.binusianId}-employee`
      }));

      const lecturers = (data.lecturers?.data || []).map((user: any) => ({
        ...user,
        sourceType: 'lecturer' as const,
        uniqueKey: `${user.binusianId}-lecturer`
      }));

      // Combine and deduplicate by binusianId
      const combined = [...employees, ...lecturers];
      const deduplicatedMap = new Map<string, ExtendedEmplecUser>();

      combined.forEach((user) => {
        const existing = deduplicatedMap.get(user.binusianId);
        if (!existing) {
          // First occurrence - add it
          deduplicatedMap.set(user.binusianId, user);
        } else {
          // Duplicate found - prefer employee over lecturer, or merge info
          if (user.sourceType === 'employee' && existing.sourceType === 'lecturer') {
            // Prefer employee data but keep both source types
            deduplicatedMap.set(user.binusianId, {
              ...user,
              uniqueKey: `${user.binusianId}-both`,
              sourceType: 'employee' as const
            });
          }
          // If existing is already employee, keep it as is
        }
      });

      return Array.from(deduplicatedMap.values());
    }
  });

  const handleOptionSubmit = (uniqueKey: string) => {
    const user = (usersData || []).find((u: ExtendedEmplecUser) => u.uniqueKey === uniqueKey);
    if (user) {
      setSelectedUser(user);
      setSearchTerm(`${user.name} (${user.binusianId})`);
      // Return the basic EmplecUser object without our extended properties
      onChange({
        binusianId: user.binusianId,
        name: user.name,
        email: user.email,
        bu: user.bu,
        namaDiv: user.namaDiv,
        level: user.level,
        fakultas: user.fakultas
      });
    }
    combobox.closeDropdown();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.currentTarget.value;
    setSearchTerm(inputValue);
    combobox.openDropdown();

    // Clear selection if input is cleared
    if (inputValue === '') {
      setSelectedUser(null);
      onChange(null);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setSelectedUser(null);
    onChange(null);
    combobox.closeDropdown();
  };

  const options = (usersData || []).map((user: ExtendedEmplecUser) => (
    <Combobox.Option value={user.uniqueKey} key={user.uniqueKey}>
      <Group gap='sm'>
        <Avatar size='sm' color='blue'>
          {user.name.charAt(0).toUpperCase()}
        </Avatar>
        <div style={{ flex: 1 }}>
          <Group justify='space-between' align='flex-start'>
            <div>
              <Text size='sm' fw={500}>
                {user.name}
              </Text>
              <Text size='xs' c='dimmed'>
                {user.binusianId} • {user.email}
              </Text>
              {(user.bu || user.namaDiv || user.fakultas) && (
                <Text size='xs' c='dimmed'>
                  {user.bu || user.namaDiv || user.fakultas}
                </Text>
              )}
            </div>
            <Badge
              size='xs'
              variant='light'
              color={user.sourceType === 'employee' ? 'blue' : 'green'}
            >
              {user.sourceType === 'employee' ? 'Staff' : 'Lecturer'}
            </Badge>
          </Group>
        </div>
      </Group>
    </Combobox.Option>
  ));

  return (
    <Stack gap='xs'>
      <Combobox
        store={combobox}
        onOptionSubmit={handleOptionSubmit}
        position='bottom-start'
        withArrow
        withinPortal={false}
      >
        <Combobox.Target>
          <InputBase
            label={label}
            placeholder={placeholder}
            required={required}
            value={searchTerm}
            onChange={handleInputChange}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => combobox.closeDropdown()}
            error={error}
            rightSection={
              isLoading ? (
                <Loader size='xs' />
              ) : selectedUser ? (
                <button
                  type='button'
                  onClick={handleClear}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  ✕
                </button>
              ) : (
                <Combobox.Chevron />
              )
            }
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {options.length > 0 ? (
              options
            ) : searchTerm.length < 2 ? (
              <Combobox.Empty>Type at least 2 characters to search</Combobox.Empty>
            ) : isLoading ? (
              <Combobox.Empty>Searching...</Combobox.Empty>
            ) : (
              <Combobox.Empty>No users found</Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>

      {selectedUser && (
        <Group gap='sm' p='xs' style={{ backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <Avatar size='sm' color='blue'>
            {selectedUser.name.charAt(0).toUpperCase()}
          </Avatar>
          <div style={{ flex: 1 }}>
            <Group justify='space-between' align='flex-start'>
              <div>
                <Text size='sm' fw={500}>
                  {selectedUser.name}
                </Text>
                <Text size='xs' c='dimmed'>
                  ID: {selectedUser.binusianId} • Email: {selectedUser.email}
                </Text>
                {(selectedUser.bu || selectedUser.namaDiv || selectedUser.fakultas) && (
                  <Text size='xs' c='dimmed'>
                    {selectedUser.bu || selectedUser.namaDiv || selectedUser.fakultas}
                  </Text>
                )}
              </div>
              <Badge
                size='xs'
                variant='light'
                color={selectedUser.sourceType === 'employee' ? 'blue' : 'green'}
              >
                {selectedUser.sourceType === 'employee' ? 'Staff' : 'Lecturer'}
              </Badge>
            </Group>
          </div>
        </Group>
      )}
    </Stack>
  );
};

export default UserSelector;
